/**
 * #2036 — wiring the vendored CKEditor to the one allow-list, in ONE place.
 *
 * Two components run an editor over `clients.login_page_message` — `splashPage.vue` and
 * `configuration/configOwnerOption.vue`. They are the only editors of the field, and if the
 * toolbar, the paste rule and the link rule were configured twice they would drift, which is
 * the defect this whole umbrella (#2034) is about. So the config and the hooks live here and
 * both components call them.
 *
 * ⚠ THE EDITOR IS NOT A SECURITY BOUNDARY, AND NOTHING HERE SHOULD BE READ AS ONE.
 *
 * The vendored editor is **CKEditor 4.12.1** (`static/assets/ckeditor/ckeditor.js`, 2019), and
 * the CKEditor 4 line has had XSS advisories published after that release. Upgrading it is its
 * own job (flagged on #2036, out of scope here). CKEditor's `allowedContent` / Advanced Content
 * Filter is a CONTENT feature, not a sandbox.
 *
 * The two boundaries are elsewhere, and neither trusts this file:
 *   SAVE      `App\Support\SafeHtml::validate()` refuses the write, naming the field.
 *   DISPLAY   `safeHtmlRules.sanitize()` strips, so a row written before #2036 is inert.
 *
 * What this file is for is the thing Ed actually asked for: a toolbar that cannot offer what the
 * rules refuse, a paste that keeps the text and says what it removed, and an http:// link
 * refused to the person's face instead of at save time.
 */
import {
  ALLOWED_TAGS,
  profileOf,
  DEFAULT_PROFILE,
  STYLE_PROPERTIES,
  STYLE_FONT_FAMILY_ENABLED,
  MAX_LENGTH,
  ckEditorAllowedContent,
  ckEditorFontNames,
  ckEditorFontSizes,
  sanitizeWithReport,
  describeRemoved,
  findProblems,
  describeProblems,
} from './safeHtmlRules.js';

/**
 * The toolbar, derived from the allow-list so it cannot offer a button the rules refuse.
 *
 * Ed, 2026-10-03: bold, italic, underline, paragraphs, line breaks, bullet/numbered lists,
 * links. `Undo`/`Redo` and `RemoveFormat` carry no markup of their own, so they are UX, not
 * content. No source-mode button: hand-written HTML is what `allowedContent` exists to filter,
 * and offering a source box invites someone to paste what the server will refuse.
 */
export function toolbar(profileName) {
  const prof = profileOf(profileName);
  const groups = [];
  const has = (t) => prof.tags.indexOf(t) !== -1;

  // ⚠ NO HEADING BUTTON, even for the policy profile, and that is a build limitation rather
  // than a choice: the vendored CKEditor carries 18 plugins and `format` — the heading dropdown
  // — is not one of them (nor `stylescombo`). Headings are supported in the MARKUP, which is
  // what makes a pasted or legacy policy render as written; a button needs that plugin vendored
  // into the repo, which is ed's call (#2041 plan §2). When it lands, it belongs here, gated on
  // `has('h2')`.
  const styleProp = (p) => Object.prototype.hasOwnProperty.call(STYLE_PROPERTIES, p);

  const basic = [];
  if (has('b') || has('strong')) basic.push('Bold');
  if (has('i') || has('em')) basic.push('Italic');
  if (has('u')) basic.push('Underline');
  if (basic.length) groups.push({ name: 'basicstyles', items: basic.concat(['RemoveFormat']) });

  // ed, 2026-10-04 — the tools that went missing. Each button is offered only when the style
  // property it writes is in the allow-list, so the toolbar cannot produce a refused save.
  const fonts = [];
  if (styleProp('font-family') && STYLE_FONT_FAMILY_ENABLED) fonts.push('Font');
  if (styleProp('font-size')) fonts.push('FontSize');
  if (fonts.length) groups.push({ name: 'styles', items: fonts });

  const colours = [];
  if (styleProp('color')) colours.push('TextColor');
  if (styleProp('background-color')) colours.push('BGColor');
  if (colours.length) groups.push({ name: 'colors', items: colours });

  const para = [];
  if (has('ol')) para.push('NumberedList');
  if (has('ul')) para.push('BulletedList');
  if (styleProp('text-align')) {
    para.push('JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock');
  }
  if (para.length) groups.push({ name: 'paragraph', items: para });

  if (has('a')) groups.push({ name: 'links', items: ['Link', 'Unlink'] });

  groups.push({ name: 'clipboard', items: ['Undo', 'Redo'] });

  return groups;
}

/**
 * The config fragment both editors spread into `CKEDITOR.replace`.
 *
 * `allowedContent` comes from `ckEditorAllowedContent()`, i.e. from the same three lists the
 * server validates against. `removePlugins` drops the features whose whole purpose is to emit
 * markup outside the allow-list — leaving them enabled would offer a button whose output the
 * server then refuses, which is a worse experience than not having the button.
 */
export function editorConfig(extra = {}) {
  // `profile` is ours, not CKEditor's — pull it out before the config is handed over.
  const profileName = extra.profile || DEFAULT_PROFILE;
  const rest = Object.assign({}, extra);
  delete rest.profile;

  return Object.assign({
    toolbar: toolbar(profileName),
    allowedContent: ckEditorAllowedContent(profileName),
    // `extraAllowedContent` is deliberately NOT set: it is the escape hatch that would let the
    // editor drift from the server.

    // ⚠ ONLY PLUGINS THAT ARE ACTUALLY IN THIS BUILD. `build-config.js` lists 18:
    //   SimpleLink basicstyles clipboard colorbutton enterkey entities floatingspace font
    //   indentlist justify lineheight link list openlink sourcedialog toolbar undo wysiwygarea
    //
    // The first version of this list named `sourcearea`, `image`, `table`, `format` and others
    // that are NOT in the build — so it removed nothing, and the Source button (which comes
    // from `sourcedialog`) survived. ed hit exactly that on stage-: "editing the html directly
    // seems a bad idea". A removePlugins list full of no-ops looks like it is doing work.
    //
    //   sourcedialog  ed's item 1 — no raw-HTML editing
    //   lineheight    writes `line-height`, which the style allow-list does not accept
    //   indentlist    writes `margin-left`, same
    removePlugins: 'sourcedialog,lineheight,indentlist',

    // ⚠ OVERRIDES THE VENDORED config.js, which sets
    //   removeButtons = 'Cut,Copy,Paste,Undo,Redo,Anchor,Underline,Strike,Subscript,Superscript'
    // and so was stripping UNDERLINE even though the toolbar above asks for it — half of
    // "formatting tools lost". Empty means: the toolbar list is the whole truth.
    removeButtons: '',

    // Generated from FONT_FAMILIES / FONT_SIZES, so the menu can only offer what the validator
    // accepts. ed: font-family "accepted only when it exactly matches a preset".
    font_names: ckEditorFontNames(),
    fontSize_sizes: ckEditorFontSizes(),
    // `More Colours…` opens a free-form picker whose output is still a colour, so the style
    // validator accepts it — left on deliberately.
    colorButton_enableMore: true,

    linkDefaultProtocol: 'https://',
    linkShowTargetTab: true,
    linkShowAdvancedTab: false,
    // No DOCTYPE/body wrapper in getData(), so what is validated is what is stored.
    fullPage: false,
    entities: false,
  }, rest);
}

/**
 * Attach the paste and link behaviour to a live editor instance.
 *
 * @param {object} editor      a CKEDITOR instance
 * @param {object} handlers
 * @param {function(string):void} handlers.onRemoved  called with a human message when a paste
 *   was trimmed — "Removed <script> and onclick on <a> — they are not allowed here."
 * @param {function(Array,string):void} [handlers.onProblems]  called on every change with the
 *   current findings and their message, for a live warning and the character counter
 */
export function constrain(editor, handlers = {}) {
  if (!editor || typeof editor.on !== 'function') return;
  const onRemoved = handlers.onRemoved || function () {};
  const onProblems = handlers.onProblems || function () {};
  const profileName = handlers.profile || DEFAULT_PROFILE;

  // ── PASTE: keep the text, strip the rest, say what went ───────────────────────────────
  //
  // Ed, 2026-10-03: "strip-on-paste with a message". CKEditor's own ACF would silently drop the
  // same nodes, and silence is the #1952 defect — "the errors aren't highlighted or identified".
  // Sanitizing here instead means ONE rule decides it (the same one the server uses) and
  // DOMPurify's `removed` journal is what the message is built from, rather than a guess.
  editor.on('paste', function (evt) {
    const incoming = evt.data && typeof evt.data.dataValue === 'string' ? evt.data.dataValue : '';
    if (incoming === '') return;

    const { html, removed } = sanitizeWithReport(incoming, profileName);
    evt.data.dataValue = html;

    if (removed.length) onRemoved(describeRemoved(removed));
  });

  // ── LINKS: https:// and mailto: only ─────────────────────────────────────────────────
  //
  // Nudge the dialog toward https and a new window. This is UX only — the authoritative refusal
  // is the full-data check below, which catches an http link however it arrived (dialog, paste,
  // or a value loaded from a row stored before #2036). Validating only inside the dialog would
  // miss all three of those.
  editor.on('dialogShow', function (e) {
    const dialog = e.data;
    if (!dialog || typeof dialog.getName !== 'function' || dialog.getName() !== 'link') return;
    try {
      if (!dialog.getValueOf('info', 'protocol')) dialog.setValueOf('info', 'protocol', 'https://');
      const target = dialog.getContentElement('target', 'linkTargetType');
      if (target && !dialog.getValueOf('target', 'linkTargetType')) {
        dialog.setValueOf('target', 'linkTargetType', '_blank');
      }
    } catch (err) {
      // Dialog internals differ across CKEditor builds; a nudge that cannot be applied is not
      // worth breaking the dialog over, and the full-data check still refuses the bad link.
    }
  });

  // ── EVERY CHANGE: the live verdict, the live counter ─────────────────────────────────
  editor.on('change', function () {
    report(editor, onProblems, profileName);
  });
  editor.on('instanceReady', function () {
    report(editor, onProblems, profileName);
  });
}

function report(editor, onProblems, profileName) {
  let data = '';
  try {
    data = editor.getData() || '';
  } catch (e) {
    return;
  }
  const prof = profileOf(profileName);
  onProblems(findProblems(data, profileName), data.length, prof.maxLength);
}

/**
 * The pre-save gate both components call.
 *
 * Returns the message to show and refuse on, or '' when it would save. The server performs the
 * same check and is the one that counts; this exists so the person is told before a round trip,
 * and so the two editors cannot disagree about when to complain.
 */
export function refusalFor(html, field = 'Login page message', profileName) {
  const problems = findProblems(html, profileName);

  return problems.length ? describeProblems(problems, field) : '';
}

export default { toolbar, editorConfig, constrain, refusalFor };
